import { useState, useEffect, useCallback, useRef } from "react";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ReferenceArea, ResponsiveContainer, Legend,
} from "recharts";
import { generateFakeDriftSeries } from "../fakeData";
import {
  captureChart, canvasToBlob, downloadBlob,
  pdfSafeText, driftHeadlineLine, driftSpikeLines,
} from "../chartExport";
import AggregationNotice from "./AggregationNotice";
import PreDataNotice from "./PreDataNotice";
import DegradationTrend from "./DegradationTrend";
import HelpTip from "./HelpTip";
import { readAggregation, buildEnvelopes, pointInterval } from "../seriesAggregation";
import { apiFetch } from "../apiFetch";
import {
  CHART_RANGE_FLOOR,
  RANGE_PRESETS,
  customRangeError,
  preDataNotice,
  windowBounds,
} from "../chartRange";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const AXIS_COLORS = {
  drift_x: "#3b82f6",
  drift_y: "#10b981",
  drift_z: "#f59e0b",
};
const MA_WINDOW = 6;

// The axes the API sends a min/max envelope for.
const DRIFT_KEYS = ["drift_x", "drift_y", "drift_z", "drift_magnitude"];

// The export draws its own legend: Recharts renders <Legend> as a sibling div,
// not inside the SVG, so capturing the SVG alone would drop the key (#83).
const LEGEND_ENTRIES = [
  { label: "Drift X", colour: AXIS_COLORS.drift_x },
  { label: "Drift Y", colour: AXIS_COLORS.drift_y },
  { label: "Drift Z", colour: AXIS_COLORS.drift_z },
];

// Ingestion outages, shaded on the chart (#167).
//
// Red where readings were lost or the loss could not be assessed, grey where
// everything was recovered. The distinction is the whole point: a restart that
// recovered cleanly is worth showing — ingestion did stop — but it is not
// missing data and must not be coloured as though it were.
const GAP_COLORS = {
  unrecoverable: "#ef4444",
  partial: "#ef4444",
  unknown: "#f59e0b",
  recovered: "#9ca3af",
};

function gapSeverity(status) {
  if (status === "unrecoverable" || status === "partial") return "lost";
  if (status === "unknown") return "unknown";
  return "recovered";
}

function formatGapDuration(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return m ? `${h} h ${m} min` : `${h} h`;
}

/**
 * Snap each gap onto the x-axis categories the chart actually plots.
 *
 * The XAxis is categorical (`dataKey="ts"`), so a ReferenceArea's x1/x2 must be
 * values present in the data — an arbitrary ISO timestamp renders nothing at
 * all. So each gap is widened to the last point at or before it and the first
 * point at or after it, which is also the honest depiction: those two readings
 * are precisely the pair the line is being drawn between.
 *
 * A gap with no point on one side (it runs to the edge of the window) is
 * clamped to that edge. A gap with no bracketing points at all — one that
 * swallowed every reading in the range — cannot be drawn as a band; it is still
 * reported in the notice above the chart, which is why that notice is driven by
 * the gap list rather than by this function's output.
 */
export function gapBands(gaps, points) {
  if (!Array.isArray(gaps) || !points.length) return [];
  const ts = points.map((p) => p.ts);
  const times = ts.map((v) => Date.parse(v));

  return gaps
    .map((gap) => {
      const start = Date.parse(gap.gap_start);
      const end = Date.parse(gap.gap_end);
      if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

      let startIdx = -1;
      for (let i = 0; i < times.length; i++) if (times[i] <= start) startIdx = i;
      let endIdx = -1;
      for (let i = times.length - 1; i >= 0; i--) if (times[i] >= end) endIdx = i;

      const x1 = ts[startIdx === -1 ? 0 : startIdx];
      const x2 = ts[endIdx === -1 ? ts.length - 1 : endIdx];
      if (x1 === x2) return null;   // nothing to shade between

      return {
        key: gap.gap_start,
        x1,
        x2,
        status: gap.status,
        severity: gapSeverity(gap.status),
        seconds: gap.gap_seconds,
      };
    })
    .filter(Boolean);
}

function movingAverage(data, key, window = MA_WINDOW) {
  return data.map((point, i) => {
    const start = Math.max(0, i - Math.floor(window / 2));
    const end = Math.min(data.length, start + window);
    const slice = data.slice(start, end);
    const avg = slice.reduce((s, p) => s + (p[key] ?? 0), 0) / slice.length;
    return { ...point, [`${key}_ma`]: parseFloat(avg.toFixed(4)) };
  });
}

function formatTimestamp(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

// Baseline window bounds are dates, not moments — the time of day is noise here.
function formatBaselineDate(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// The API now reports why baseline readings were dropped, not just how many
// (#65) — "excluded as triggered" was wrong for rows excluded for carrying no
// angle data. A response from an older API carries only the total, so fall back
// to the bare count rather than attributing a reason it never stated.
function excludedReasons(baseline) {
  const reasons = [];
  if (baseline.n_excluded_triggered > 0)
    reasons.push(`${baseline.n_excluded_triggered} triggered`);
  if (baseline.n_excluded_no_angle_data > 0)
    reasons.push(`${baseline.n_excluded_no_angle_data} with no angle data`);
  return reasons.length ? ` (${reasons.join(", ")})` : "";
}

// `help` names a topic in helpTopics.js, never a sentence — the label and the
// explanation must not be two versions of the same claim (#171).
function Stat({ label, value, highlight, help, t, simulated }) {
  return (
    <div style={styles.stat}>
      <span style={styles.statLabel}>
        {label}
        {help && <HelpTip topic={help} t={t} simulated={simulated} />}
      </span>
      <span style={{ ...styles.statValue, color: highlight ? "#ef4444" : "#111827" }}>{value}</span>
    </div>
  );
}

// `simulated` renders the chart from generated data and makes no network
// request at all — used for auth tiers below 'superuser', which must never
// touch /api/. See fakeData.js.
export default function TowerDriftChart({ deviceId, apiBaseUrl, label, t = (k) => k, simulated = false }) {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState([]);
  const [baseline, setBaseline] = useState(null);
  const [alertCount, setAlertCount] = useState(0);
  // Readings the detector filtered, from raw_spike_count. `spike_count` is
  // buckets containing at least one spike, which on an aggregated series is a
  // smaller number — and the notice below has always said "readings" (#141).
  // Older API builds send only the bucket figure, so fall back to it rather than
  // reporting nothing.
  const [spikeReadings, setSpikeReadings] = useState(0);
  // Spans in this range where ingestion was not running (#167).
  //
  // `null` means the API could not tell us — a server too old to send the field,
  // or a gap table that would not read. It must render differently from `[]`:
  // an empty list is "there were no outages", and null is "nobody knows", and
  // drawing an unbroken line for the second is the failure #167 is about.
  const [gaps, setGaps] = useState([]);
  const [driftThreshold, setDriftThreshold] = useState(1.0);
  const [editingThreshold, setEditingThreshold] = useState(false);
  const [thresholdInput, setThresholdInput] = useState("1.0");
  const [thresholdSaving, setThresholdSaving] = useState(false);
  const [thresholdError, setThresholdError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [visibleAxes, setVisibleAxes] = useState({ x: true, y: true, z: true });
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [customError, setCustomError] = useState(null);
  const [committedCustom, setCommittedCustom] = useState({ from: "", to: "" });
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null); // null = idle, 0-100 = progress
  // "auto" lets the API decide the point budget; "raw" asks for the readings as
  // received, which the API serves or refuses with raw_capped (#108).
  const [plotMode, setPlotMode] = useState("auto");
  const [agg, setAgg] = useState(() => readAggregation(null));
  // When this device started reporting, from the API. Drives the pre-data
  // notice; null in simulated mode, where the generator has no install date.
  const [firstReading, setFirstReading] = useState(null);

  const cardRef = useRef(null);

  useEffect(() => {
    const styleId = "export-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
      document.head.appendChild(style);
    }
  }, []);

  // From the shared list so all four charts offer the same set (#231).
  const RANGE_OPTIONS = RANGE_PRESETS.map(({ key, value }) => ({ label: t(key), value }));

  const toggleAxis = (axis) => setVisibleAxes((prev) => ({ ...prev, [axis]: !prev[axis] }));

  const buildUrl = () => {
    // Only appended when raw was actually asked for, so the default request is
    // byte-for-byte the one this endpoint has always received (#108).
    const raw = plotMode === "raw" ? "&raw=1" : "";
    if (range === "custom" && committedCustom.from && committedCustom.to) {
      return `${apiBaseUrl}/api/drift?device_id=${encodeURIComponent(deviceId)}&start=${committedCustom.from}&end=${committedCustom.to}${raw}`;
    }
    return `${apiBaseUrl}/api/drift?device_id=${encodeURIComponent(deviceId)}&range=${range}${raw}`;
  };

  // Custom is selected but Apply has not been pressed on a complete pair of
  // dates yet, so there is no range to ask for. buildUrl() would fall through to
  // `range=custom`, and the API rejects that with a 400 — which showed up as
  // "Failed to load" the instant the button was clicked, before the user could
  // type anything (#91). Falling back to a default range instead would be worse:
  // it charts 7 days while the user is looking at an empty Custom form.
  const customPending = range === "custom" && !(committedCustom.from && committedCustom.to);

  // Whether this window opens before the device existed, and if so which of the
  // two cases it is. Null when the device has readings for the whole window, or
  // when the API did not say when it started (#184).
  const windowStart = windowBounds(range, committedCustom);
  const preData = preDataNotice(firstReading, windowStart.from, windowStart.to);

  const fetchData = useCallback(async () => {
    if (customPending) { setError(null); setLoading(false); return; }
    try {
      let json;
      if (simulated) {
        json = generateFakeDriftSeries(deviceId, range);
      } else {
        const url = buildUrl();
        const res = await apiFetch(url);
        if (!res.ok) throw new Error(`API error ${res.status}`);
        json = await res.json();
      }
      setBaseline(json.baseline);
      setAlertCount(json.alert_count ?? 0);
      setSpikeReadings(json.raw_spike_count ?? json.spike_count ?? 0);
      // Simulated data describes no real deployment, so it must never claim an
      // outage — or the absence of one. Otherwise: the API's list, or null when
      // it did not send one.
      setGaps(simulated ? [] : (json.ingestion_gaps ?? null));
      // Same rule as the gaps above: simulated data describes no real
      // deployment, so it must not claim an install date either.
      setFirstReading(simulated ? null : (json.first_reading ?? null));
      setAgg(readAggregation(json));
      const thresh = json.drift_threshold ?? 1.0;
      setDriftThreshold(thresh);
      setThresholdInput(String(thresh));
      setLastUpdated(new Date());
      let points = json.data.map((d) => ({ ...d, ts: d.timestamp }));
      points = movingAverage(points, "drift_x");
      points = movingAverage(points, "drift_y");
      points = movingAverage(points, "drift_z");
      // Turns the API's min/max into the [lo, hi] pairs the bands render from.
      setData(buildEnvelopes(points, DRIFT_KEYS));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, apiBaseUrl, range, committedCustom, customPending, simulated, plotMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // No polling either while the range is undecided — a 5-minute timer that can
    // only produce a rejected request.
    if (customPending) { setLoading(false); return; }
    setLoading(true);
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData, customPending]);

  // Peak drift takes each point's measured maximum where the series is
  // aggregated. Reading drift_magnitude alone reported the peak of the means,
  // which on a 28-reading bucket is not the peak of anything the sensor
  // measured, and was always lower than the real one (#108).
  const maxMagnitude = data.length > 0
    ? Math.max(...data.map((d) => d.drift_magnitude_max ?? d.drift_magnitude ?? 0)).toFixed(3)
    : "—";
  // The last point is the newest raw reading whenever the series was aggregated,
  // so "current drift" is a reading rather than the mean of the final bucket.
  const latestDrift = data.length > 0 ? data[data.length - 1] : null;
  // The denominator alert_count is counted against: the API excludes the
  // appended latest tip from it, because that reading is already inside the
  // final bucket. Counting it here would report 300 of 301.
  const plottedPoints = data.filter((d) => !d.is_latest).length;

  // Ingestion outages overlapping this range, and the worst thing true of them.
  // Computed from the gap list rather than from the bands, so an outage that
  // swallowed every reading — and therefore cannot be shaded between two points
  // — is still reported.
  const bands = gapBands(gaps, data);
  const lostGaps = (gaps ?? []).filter((g) => gapSeverity(g.status) === "lost");
  const unknownGaps = (gaps ?? []).filter((g) => gapSeverity(g.status) === "unknown");
  const recoveredGaps = (gaps ?? []).filter((g) => gapSeverity(g.status) === "recovered");
  const gapSeconds = (list) => list.reduce((s, g) => s + (Number(g.gap_seconds) || 0), 0);
  // What is gone for good, as a duration. A count of lost readings is not
  // knowable — the uplinks TTN no longer holds cannot be counted by anyone.
  const unrecoverableSeconds = lostGaps.reduce(
    (s, g) => s + (Number(g.unrecoverable_seconds) || 0), 0
  );

  // ── Export handlers ───────────────────────────────────────────────────────
  // Both exports read `committedCustom` for the report header, so an export
  // started before Apply would carry an empty range (" → ") over a chart that
  // is not on screen. The buttons are disabled in that state; the guards make
  // the handlers safe on their own.
  const exportDisabled = exportProgress !== null || customPending;

  const exportJpeg = async () => {
    if (exportDisabled) return;
    setExportProgress({ pct: 30, label: "Capturing chart…" });
    await new Promise(r => setTimeout(r, 50)); // let UI paint
    try {
      const chartDiv = cardRef.current.querySelector(".recharts-wrapper");
      if (!chartDiv) { setExportProgress(null); return; }
      const canvas = await captureChart(chartDiv, "jpeg", { legend: LEGEND_ENTRIES });
      setExportProgress({ pct: 80, label: "Saving image…" });
      await new Promise(r => setTimeout(r, 50));
      // Blob rather than a data: URL — encoding is async and iOS Safari refuses
      // very large data: URLs outright (#83).
      const blob = await canvasToBlob(canvas, "image/jpeg", 0.95);
      downloadBlob(blob, `${deviceId}_drift_chart.jpg`);
      setExportProgress({ pct: 100, label: "Done!" });
      setTimeout(() => setExportProgress(null), 1200);
    } catch (e) {
      console.error(e);
      setExportProgress(null);
    }
  };
  
  const exportPdf = async () => {
    if (exportDisabled) return;
    try {
      setExportProgress({ pct: 10, label: "Preparing…" });
      await new Promise(r => setTimeout(r, 50));
  
      const { jsPDF } = await import("jspdf");
      setExportProgress({ pct: 25, label: "Loading libraries…" });
      await new Promise(r => setTimeout(r, 50));
  
      const chartDiv = cardRef.current.querySelector(".recharts-wrapper");
      if (!chartDiv) { setExportProgress(null); return; }

      setExportProgress({ pct: 45, label: "Capturing chart…" });
      await new Promise(r => setTimeout(r, 50));

      // Scale comes from the shared pixel budget rather than a fixed multiplier:
      // a fixed 3× made the canvas grow with the viewport, which is what hung
      // Chrome and crashed Safari on a wide monitor. See chartExport.js.
      const canvas = await captureChart(chartDiv, "pdf", { legend: LEGEND_ENTRIES });

      setExportProgress({ pct: 70, label: "Generating PDF…" });
      await new Promise(r => setTimeout(r, 50));
  
      const pageW = 595.28; // A4 portrait pts
      const pageH = 841.89;
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  
      let y = 30;
      // Every string reaching pdf.text() goes through pdfSafeText: the built-in
      // Helvetica is a WinAnsi font, which has no "→" and none of the
      // Montenegrin diacritics (#141).
      const line = (text) => pdf.text(pdfSafeText(text), 30, y);

      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(17, 24, 39);
      line(`${t("exportTitle")} — ${label ?? deviceId}`);

      y += 18;
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(120, 120, 120);
      line(`${t("exportDeviceId")}: ${deviceId}`);
      y += 14;
      line(`${t("exportedAt")}: ${new Date().toLocaleString()}`);
      y += 14;
      const rangeText = range === "custom"
        ? `${committedCustom.from} → ${committedCustom.to}`
        : (RANGE_OPTIONS.find((o) => o.value === range)?.label ?? range);
      line(`${t("exportRangeLabel")}: ${rangeText}`);

      if (baseline) {
        y += 14;
        pdf.setTextColor(40, 40, 40);
        line(t("exportBaselineLine", {
          x: baseline.angle_x.toFixed(2),
          y: baseline.angle_y.toFixed(2),
          z: baseline.angle_z.toFixed(2),
        }));
      }

      y += 14;
      pdf.setTextColor(alertCount > 0 ? 185 : 6, alertCount > 0 ? 28 : 95, alertCount > 0 ? 28 : 70);
      line(driftHeadlineLine({
        alertCount,
        pointCount: plottedPoints,
        peakDrift: maxMagnitude,
        threshold: driftThreshold,
        t,
      }));

      // The headline's peak is the peak of the spike-filtered series, so it does
      // not survive on its own: without these lines a reader sees 13.006° and
      // has no reason to suspect a 57.7° excursion was excluded (#141).
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      for (const spikeLine of driftSpikeLines({ spikeCount: spikeReadings, t })) {
        y += 12;
        line(spikeLine);
      }
      pdf.setFontSize(10);

      y += 20;
      pdf.setDrawColor(220, 220, 220);
      pdf.line(30, y, pageW - 30, y);
      y += 10;
  
      const availH = pageH - y - 20;
      const aspect = canvas.width / canvas.height;
      let chartW = pageW - 60;
      let chartH = chartW / aspect;
      if (chartH > availH) { chartH = availH; chartW = chartH * aspect; }
      const chartX = 30 + (pageW - 60 - chartW) / 2;
  
      pdf.addImage(canvas, "JPEG", chartX, y, chartW, chartH);
  
      setExportProgress({ pct: 90, label: "Saving…" });
      await new Promise(r => setTimeout(r, 50));
  
      // output("blob") + downloadBlob rather than pdf.save(), which uses the
      // same unreliable <a download> path on iOS Safari (#83).
      downloadBlob(pdf.output("blob"), `${deviceId}_drift_report.pdf`);
      setExportProgress({ pct: 100, label: "Done!" });
      setTimeout(() => setExportProgress(null), 1200);
    } catch (e) {
      console.error("PDF export failed:", e);
      setExportProgress(null);
    }
  };

  // ── Sub-components ────────────────────────────────────────────────────────
  function AlertBadge({ count }) {
    if (count === 0)
      return <span style={styles.badgeOk}>● {t("normalStatus")}</span>;
    return <span style={styles.badgeAlert}>⚠ {count} {t("alertStatus")}</span>;
  }

  function DriftTooltip({ active, payload, label: ttLabel }) {
    if (!active || !payload?.length) return null;
    const point = payload[0]?.payload;
    // An aggregated point covers an interval, and each axis has a real min and
    // max inside it. Showing only the mean against one member's timestamp is the
    // labelling defect #108 reported.
    const interval = pointInterval(point);
    return (
      <div style={styles.tooltip}>
        <p style={styles.tooltipTime}>
          {interval
            ? t("aggIntervalTooltip", {
                n: interval.n,
                start: formatTimestamp(interval.start),
                end: formatTimestamp(interval.end),
              })
            : formatTimestamp(ttLabel)}
        </p>
        {["x", "y", "z"].map((axis) => {
          const key = `drift_${axis}`;
          const low = point[`${key}_min`];
          const high = point[`${key}_max`];
          return (
            <p key={axis} style={{ color: AXIS_COLORS[key], margin: "2px 0" }}>
              Drift {axis.toUpperCase()}: {point[key]?.toFixed(3)}°
              {typeof low === "number" && typeof high === "number" &&
                ` (${low.toFixed(3)}…${high.toFixed(3)}°)`}
            </p>
          );
        })}
        <p style={{ color: "#6b7280", marginTop: 4, fontSize: 12 }}>
          Magnitude: {point.drift_magnitude?.toFixed(3)}°
          {typeof point.drift_magnitude_max === "number" &&
            ` (peak ${point.drift_magnitude_max.toFixed(3)}°)`}
        </p>
        {point.is_latest && (
          <p style={{ color: "#9ca3af", marginTop: 4, fontSize: 12 }}>{t("aggLatestTooltip")}</p>
        )}
        {point.has_alert && (
          <p style={{ color: "#ef4444", marginTop: 4, fontSize: 12 }}>
            ⚠ {t("thresholdExceeded")}
          </p>
        )}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <div ref={cardRef} style={{ ...styles.card, maxWidth: 1600 }}>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>{label ?? deviceId}</h2>
          <p style={styles.subtitle}>{deviceId}</p>
        </div>
        <div style={styles.headerRight}>
          <AlertBadge count={alertCount} />
			<div style={styles.exportRow}>
			  <button
			  	style={{ ...styles.exportBtn, opacity: exportDisabled ? 0.5 : 1 }}
			  	onClick={exportJpeg}
			  	disabled={exportDisabled}
			  >
			  	⬇ JPEG
			  </button>
			  <button
			  	style={{ ...styles.exportBtn, opacity: exportDisabled ? 0.5 : 1 }}
			  	onClick={exportPdf}
			  	disabled={exportDisabled}
			  >
			  	⬇ PDF
			  </button>
			</div>
			{/* Progress bar */}
			{exportProgress !== null && (
			<div style={styles.progressWrap}>
				<div style={styles.spinner} />
				<span style={styles.progressLabel}>{exportProgress.label}</span>
			</div>
			)}
        </div>
      </div>

      {/* Range selector */}
      <div style={styles.rangeRow}>
        {RANGE_OPTIONS.map((opt) => (
          <button key={opt.value} onClick={() => setRange(opt.value)}
            style={range === opt.value ? styles.rangeActive : styles.rangeBtn}>
            {opt.label}
          </button>
        ))}
        <button onClick={() => setRange("custom")}
          style={range === "custom" ? styles.rangeActive : styles.rangeBtn}>
          {t("rangeCustom")}
        </button>
        <span style={styles.lastUpdated}>
          {lastUpdated ? `${t("updated")} ${lastUpdated.toLocaleTimeString()}` : t("loading")}
        </span>
      </div>

      {/* Custom date range picker */}
      {range === "custom" && (
        <div style={styles.customRangeRow}>
          <label style={styles.customLabel}>{t("customFrom")}</label>
          <input type="date" value={customFrom}
            min={CHART_RANGE_FLOOR}
            max={customTo || new Date().toISOString().split("T")[0]}
            onChange={(e) => { setCustomFrom(e.target.value); setCustomError(null); }}
            style={styles.dateInput}
          />
          {/* One click back to the start of the data (#231). Sets the field and
              stops there rather than applying: on this device a full-range
              drift query is ~15 s, and a control that fires that before the
              operator can see what it chose is worse than the one it replaces. */}
          <button type="button" style={styles.fromStartBtn}
            title={t("fromStartTitle")}
            onClick={() => { setCustomFrom(CHART_RANGE_FLOOR); setCustomError(null); }}>
            {t("fromStart")}
          </button>
          <label style={styles.customLabel}>{t("customTo")}</label>
          <input type="date" value={customTo} min={customFrom || CHART_RANGE_FLOOR}
            max={new Date().toISOString().split("T")[0]}
            onChange={(e) => { setCustomTo(e.target.value); setCustomError(null); }}
            style={styles.dateInput}
          />
          <button style={styles.applyBtn} onClick={() => {
            const key = customRangeError(customFrom, customTo);
            if (key) { setCustomError(t(key)); return; }
            setCustomError(null);
            setCommittedCustom({ from: customFrom, to: customTo });
          }}>
            {t("apply")}
          </button>
          {customError && <span style={styles.customError}>{customError}</span>}
        </div>
      )}

      {/* Axis toggles + threshold editor */}
      <div style={styles.axisToggles}>
        {["x", "y", "z"].map((axis) => (
          <label key={axis} style={styles.axisToggle}>
            <input type="checkbox" checked={visibleAxes[axis]} onChange={() => toggleAxis(axis)}
              style={{ accentColor: AXIS_COLORS[`drift_${axis}`], marginRight: 6, width: 18, height: 18, cursor: "pointer" }}
            />
            <span style={{ color: AXIS_COLORS[`drift_${axis}`], fontWeight: 600 }}>
              {t(`${axis}Axis`)}
            </span>
          </label>
        ))}
        {/* Simulated mode shows the threshold read-only — saving it is a PUT to
            /api/drift/threshold, which non-superusers must not reach. */}
        <div style={styles.thresholdEditor}>
          {simulated ? (
            <span style={styles.thresholdLabel}>
              {t("alertThreshold")} ±{driftThreshold}°
            </span>
          ) : editingThreshold ? (
            <>
              <span style={styles.thresholdLabel}>{t("alertThreshold")} ±</span>
              <input type="number" value={thresholdInput} min="0.1" max="90" step="0.1"
                onChange={(e) => setThresholdInput(e.target.value)}
                style={styles.thresholdInput} autoFocus
              />
              <span style={styles.thresholdLabel}>°</span>
              <button style={styles.thresholdSaveBtn} disabled={thresholdSaving}
                onClick={async () => {
                  const val = parseFloat(thresholdInput);
                  if (isNaN(val) || val <= 0 || val > 90) { setThresholdError("0.1–90"); return; }
                  setThresholdSaving(true); setThresholdError(null);
                  try {
                    const res = await apiFetch(`${apiBaseUrl}/api/drift/threshold`, {
                      method: "PUT",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ device_id: deviceId, drift_threshold: val }),
                    });
                    if (!res.ok) throw new Error(`Server error ${res.status}`);
                    setDriftThreshold(val);
                    setEditingThreshold(false);
                  } catch (err) {
                    setThresholdError("Failed to save");
                  } finally {
                    setThresholdSaving(false);
                  }
                }}>
                {thresholdSaving ? "…" : "✓"}
              </button>
              <button style={styles.thresholdCancelBtn} onClick={() => {
                setEditingThreshold(false); setThresholdInput(String(driftThreshold)); setThresholdError(null);
              }}>✕</button>
              {thresholdError && <span style={{ color: "#ef4444", fontSize: 11 }}>{thresholdError}</span>}
            </>
          ) : (
            <button style={styles.thresholdDisplayBtn} onClick={() => setEditingThreshold(true)}>
              ✎ {t("alertThreshold")} ±{driftThreshold}°
            </button>
          )}
        </div>
      </div>

      {/* Summary stats — peak and current drift describe the loaded window, so
          they are held back with the chart rather than left over from the range
          the user just navigated away from. */}
      {baseline && !customPending && (
        <div style={styles.statsRow}>
          {/* One help control for the baseline group, on the first of the three
              — they are one figure per axis of one baseline, and three icons in
              a row would read as three different claims. */}
          <Stat label={t("baselineX")} value={`${baseline.angle_x.toFixed(2)}°`}
            help="driftBaseline" t={t} simulated={simulated} />
          <Stat label={t("baselineY")} value={`${baseline.angle_y.toFixed(2)}°`} />
          <Stat label={t("baselineZ")} value={`${baseline.angle_z.toFixed(2)}°`} />
          <Stat label={t("peakDrift")} value={`${maxMagnitude}°`} highlight={parseFloat(maxMagnitude) > driftThreshold}
            help="driftValue" t={t} simulated={simulated} />
          {latestDrift && (
            <Stat label={t("currentDrift")} value={`${latestDrift.drift_magnitude?.toFixed(3)}°`} highlight={latestDrift.has_alert}
              help="driftValue" t={t} simulated={simulated} />
          )}
        </div>
      )}

      {/* Which readings the baseline was computed from — makes a suspicious
          baseline diagnosable without opening the API response. Held back with
          the stats above while Custom is pending, for the same reason. */}
      {baseline?.baseline_from && !customPending && (
        <p style={{
          fontSize: 11, color: '#9ca3af',
          margin: '4px 0 12px', fontStyle: 'italic'
        }}>
          Baseline from{' '}
          {formatBaselineDate(baseline.baseline_from)} to{' '}
          {formatBaselineDate(baseline.baseline_to)}
          {' — '}
          {baseline.n_samples} normal {baseline.n_samples === 1 ? 'reading' : 'readings'}
          {baseline.n_excluded > 0 &&
            `, ${baseline.n_excluded} excluded${excludedReasons(baseline)}`}
        </p>
      )}

      {/* Why the chart is blank before a given date, when it is. Deliberately
          not gated on data.length: the case that most needs explaining is the
          one where the window ends before the device existed and there is
          nothing plotted at all. */}
      {!customPending && !loading && !error && (
        <PreDataNotice notice={preData} t={t} />
      )}

      {/* What the points are, and the raw/aggregated control. Renders nothing
          when the series was not decimated — at 24 h this device's 189 readings
          are already plotted as measured. */}
      {!customPending && !loading && !error && data.length > 0 && (
        <AggregationNotice
          agg={agg}
          mode={plotMode}
          onModeChange={setPlotMode}
          t={t}
          canToggle={!simulated}
        />
      )}

      {/* Spike notice — only when the backend replaced readings in this range */}
      {spikeReadings > 0 && !customPending && (
        <div style={styles.spikeNotice}>
          <span style={styles.spikeNoticeText}>
            ⚡ {spikeReadings === 1 ? t("spikeNoticeOne") : t("spikeNotice", { n: spikeReadings })}
          </span>
        </div>
      )}

      {/* Ingestion gap notice (#167).
          Driven by the gap list, not by the shaded bands: an outage that
          swallowed every reading in the range has no two points to shade
          between, and that is exactly the case that most needs saying. Ordered
          worst-first, and the lost/unknown notices are never collapsed into the
          recovered one. */}
      {!customPending && lostGaps.length > 0 && (
        <div style={styles.gapNoticeLost}>
          {t("ingestGapLost", {
            d: formatGapDuration(gapSeconds(lostGaps)),
            u: formatGapDuration(unrecoverableSeconds),
          })}
        </div>
      )}
      {!customPending && unknownGaps.length > 0 && (
        <div style={styles.gapNoticeUnknown}>
          {t("ingestGapUnknown", { d: formatGapDuration(gapSeconds(unknownGaps)) })}
        </div>
      )}
      {!customPending && recoveredGaps.length > 0 && (
        <div style={styles.gapNoticeRecovered}>
          {t("ingestGapRecovered", { d: formatGapDuration(gapSeconds(recoveredGaps)) })}
        </div>
      )}

      {/* Chart */}
      {customPending ? (
        <div style={styles.placeholder}>{t("customRangePrompt")}</div>
      ) : loading ? (
        <div style={styles.placeholder}>{t("driftChartLoading")}</div>
      ) : error ? (
        <div style={styles.errorBox}>{t("driftChartError")}: {error}</div>
      ) : data.length === 0 ? (
        <div style={styles.placeholder}>{t("driftChartNoData")}</div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="ts"
              tickFormatter={(v) => new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
              tick={{ fontSize: 11, fill: "#6b7280" }} minTickGap={40}
            />
            <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} tickFormatter={(v) => `${v}°`}
              label={{ value: "Drift (°)", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#9ca3af" } }}
            />
            <Tooltip content={<DriftTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
              formatter={(value) =>
                value === "drift_x" ? t("xAxis") : value === "drift_y" ? t("yAxis") : t("zAxis")
              }
            />
            {/* Ingestion outages (#167), under everything else so the traces
                stay readable. Each band spans the two readings the line is
                actually drawn between, because those are the two points whose
                connection is being asserted without evidence. */}
            {bands.map((b) => (
              <ReferenceArea
                key={b.key}
                x1={b.x1}
                x2={b.x2}
                fill={GAP_COLORS[b.status] ?? GAP_COLORS.unknown}
                fillOpacity={b.severity === "recovered" ? 0.08 : 0.16}
                stroke="none"
                ifOverflow="extendDomain"
              />
            ))}
            <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="4 2" />
            <ReferenceLine y={driftThreshold} stroke="#ef4444" strokeDasharray="6 3"
              label={{ value: `${t("alertThreshold")} ±${driftThreshold}°`, position: "insideTopRight", fontSize: 11, fill: "#ef4444" }}
            />
            <ReferenceLine y={-driftThreshold} stroke="#ef4444" strokeDasharray="6 3" />
            {/* Min/max envelope per axis, under the traces. Where the series is
                aggregated this is the range of readings the point covers — both
                bounds real — so a transient inside a bucket is visible instead
                of being averaged into a value that never occurred (#108).
                Absent on a raw series, where each point is one reading. */}
            {visibleAxes.x && <Area dataKey="drift_x_band" stroke="none" fill={AXIS_COLORS.drift_x} fillOpacity={0.12} connectNulls={false} activeDot={false} legendType="none" isAnimationActive={false} />}
            {visibleAxes.y && <Area dataKey="drift_y_band" stroke="none" fill={AXIS_COLORS.drift_y} fillOpacity={0.12} connectNulls={false} activeDot={false} legendType="none" isAnimationActive={false} />}
            {visibleAxes.z && <Area dataKey="drift_z_band" stroke="none" fill={AXIS_COLORS.drift_z} fillOpacity={0.12} connectNulls={false} activeDot={false} legendType="none" isAnimationActive={false} />}
            {visibleAxes.x && <Line type="monotone" dataKey="drift_x" stroke={AXIS_COLORS.drift_x} dot={false} strokeWidth={1} strokeOpacity={0.25} legendType="none" activeDot={false} />}
            {visibleAxes.y && <Line type="monotone" dataKey="drift_y" stroke={AXIS_COLORS.drift_y} dot={false} strokeWidth={1} strokeOpacity={0.25} legendType="none" activeDot={false} />}
            {visibleAxes.z && <Line type="monotone" dataKey="drift_z" stroke={AXIS_COLORS.drift_z} dot={false} strokeWidth={1} strokeOpacity={0.25} legendType="none" activeDot={false} />}
            {visibleAxes.x && <Line type="monotone" dataKey="drift_x_ma" name="drift_x" stroke={AXIS_COLORS.drift_x} dot={false} strokeWidth={2.5} activeDot={{ r: 4 }} />}
            {visibleAxes.y && <Line type="monotone" dataKey="drift_y_ma" name="drift_y" stroke={AXIS_COLORS.drift_y} dot={false} strokeWidth={2.5} activeDot={{ r: 4 }} />}
            {visibleAxes.z && <Line type="monotone" dataKey="drift_z_ma" name="drift_z" stroke={AXIS_COLORS.drift_z} dot={false} strokeWidth={2.5} activeDot={{ r: 4 }} />}
          </ComposedChart>
        </ResponsiveContainer>
      )}

      {baseline?.is_partial && (
        <p style={styles.warning}>
          ⚠ {t("baselineWarning", { n: baseline.n_samples })}
        </p>
      )}
    </div>

    {/* Drift velocity and acceleration (#130). A sibling card rather than a
        section of this one: it is deliberately not tied to the range selector
        above — it always reports a trailing 30-day window and says so — and the
        chart exports capture only the chart. */}
    <DegradationTrend
      deviceId={deviceId}
      apiBaseUrl={apiBaseUrl}
      t={t}
      simulated={simulated}
    />
    </>
  );
}

const styles = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "20px 24px", fontFamily: "'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 },
  headerRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 },
  exportRow: { display: "flex", gap: 6 },
  exportBtn: { padding: "4px 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  progressWrap: { display: "flex", alignItems: "center", gap: 10, marginTop: 8 },
  progressTrack: { flex: 1, height: 6, background: "#e5e7eb", borderRadius: 99, overflow: "hidden" },
  progressBar: { height: "100%", background: "#3b82f6", borderRadius: 99, transition: "width 0.3s ease" },
  progressLabel: { fontSize: 11, color: "#6b7280", whiteSpace: "nowrap", minWidth: 110 },
  progressBarPulsing: {
  backgroundImage: "linear-gradient(90deg, #3b82f6 25%, #93c5fd 50%, #3b82f6 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.2s infinite linear",
  },
  spinner: {
  width: 16,
  height: 16,
  borderRadius: "50%",
  border: "2px solid #e5e7eb",
  borderTopColor: "#3b82f6",
  animation: "spin 0.8s linear infinite",
  flexShrink: 0,
  },
  title: { margin: 0, fontSize: 18, fontWeight: 600, color: "#111827" },
  subtitle: { margin: "2px 0 0", fontSize: 12, color: "#9ca3af" },
  badgeOk: { background: "#d1fae5", color: "#065f46", padding: "3px 10px", borderRadius: 12, fontSize: 13, fontWeight: 500 },
  badgeAlert: { background: "#fee2e2", color: "#991b1b", padding: "3px 10px", borderRadius: 12, fontSize: 13, fontWeight: 500 },
  rangeRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 16 },
  rangeBtn: { padding: "4px 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: 13 },
  rangeActive: { padding: "4px 12px", border: "1px solid #3b82f6", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  lastUpdated: { marginLeft: "auto", fontSize: 12, color: "#9ca3af" },
  statsRow: { display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16, padding: "12px 16px", background: "#f9fafb", borderRadius: 6 },
  stat: { display: "flex", flexDirection: "column", gap: 2 },
  // minHeight is the HelpTip button's box (16 px icon + 4 px padding each side).
  // Only some labels carry one, and without a fixed row height those labels
  // grew taller and pushed their values below the rest of the row.
  statLabel: { fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", display: "inline-flex", alignItems: "center", gap: 2, minHeight: 24 },
  statValue: { fontSize: 16, fontWeight: 600 },
  placeholder: { height: 320, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 14 },
  errorBox: { height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", fontSize: 14, background: "#fef2f2", borderRadius: 6 },
  tooltip: { background: "#1f2937", border: "none", borderRadius: 6, padding: "10px 14px", color: "#f9fafb", fontSize: 13 },
  tooltipTime: { margin: "0 0 6px", color: "#9ca3af", fontSize: 12 },
  customRangeRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" },
  customLabel: { fontSize: 13, color: "#6b7280", fontWeight: 500 },
  dateInput: { padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, color: "#374151", background: "#f9fafb" },
  applyBtn: { padding: "4px 14px", border: "1px solid #3b82f6", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  // Quieter than applyBtn on purpose: this fills a field, it does not run the
  // query, and it must not compete with Apply for the eye (#231).
  fromStartBtn: { padding: "4px 10px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#6b7280", cursor: "pointer", fontSize: 12 },
  customError: { fontSize: 12, color: "#ef4444" },
  axisToggles: { display: "flex", gap: 16, marginBottom: 12, alignItems: "center", flexWrap: "wrap" },
  axisToggle: { display: "flex", alignItems: "center", cursor: "pointer", fontSize: 14, userSelect: "none" },
  thresholdEditor: { display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" },
  thresholdLabel: { fontSize: 13, color: "#6b7280" },
  thresholdInput: { width: 64, padding: "3px 6px", border: "1px solid #ef4444", borderRadius: 4, fontSize: 13, color: "#111827", textAlign: "center" },
  thresholdSaveBtn: { padding: "3px 8px", border: "1px solid #10b981", borderRadius: 4, background: "#d1fae5", color: "#065f46", cursor: "pointer", fontSize: 13, fontWeight: 700 },
  thresholdCancelBtn: { padding: "3px 8px", border: "1px solid #d1d5db", borderRadius: 4, background: "#f9fafb", color: "#6b7280", cursor: "pointer", fontSize: 13 },
  thresholdDisplayBtn: { padding: "3px 10px", border: "1px solid #fca5a5", borderRadius: 4, background: "#fff1f2", color: "#991b1b",cursor: "pointer", fontSize: 13, fontWeight: 500 },
  warning: { marginTop: 12, fontSize: 12, color: "#92400e", background: "#fffbeb", padding: "6px 12px", borderRadius: 4 },
  spikeNotice: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "6px 12px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 4 },
  spikeNoticeText: { fontSize: 11, color: "#92400e" },
  // Ingestion gap notices (#167). Three weights, because the three outcomes are
  // genuinely different: red for readings that are gone, amber for an outage we
  // could not assess, grey for one that was fully recovered. Colouring a clean
  // restart red would train the operator to ignore the red one.
  gapNoticeLost: { fontSize: 11, color: "#991b1b", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 4, padding: "6px 12px", marginBottom: 8 },
  gapNoticeUnknown: { fontSize: 11, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 4, padding: "6px 12px", marginBottom: 8 },
  gapNoticeRecovered: { fontSize: 11, color: "#4b5563", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 4, padding: "6px 12px", marginBottom: 8 },
};